/**
 * paperUsageApi — 「이 문제가 담긴 시험지」를 서버에 묻는다.
 *
 * 계약: `docs/problem-hub-v2/BE-20260901-reply-paper-usage-by-problem.md`(dev `e8b6700`, 2026-09-01).
 * `GET /papers?problem_id=` 한 번 + `GET /folders` 한 번이 전부다. `problem_id` 를 준 요청의 각 항목에만
 * `matched_order_no`(**0부터** — 상세 `items[].order_no` 와 같은 기준, 회신 §2 4-5)·`matched_points`·
 * `assign_count`(살아 있는 배정 수) 가 동봉된다.
 *
 * 2026-09-01 이전의 이 파일은 브라우저가 시험지를 하나씩 열어 문항을 대조하는 임시 우회였다
 * (요청서 `FE-20260901-paper-usage-by-problem.md` §3 — dev 15장·17왕복·0.6~1.0초). 계약이 와서
 * 훑기·색인·진행률·「언제 훑었는지」·[다시 훑기]를 전부 걷었다 — 목록 한 번이면 끝이라 훑을 것도, 낡을 것도 없다.
 *
 * **배포 전 서버를 가려낸다.** 옛 서버는 모르는 파라미터를 조용히 무시하고 **전체 목록을 200 으로** 준다
 * (요청서 §1-1). 그 응답을 그대로 그리면 「이 문제가 모든 시험지에 들어 있다」는 거짓말이 된다.
 * 회신 §2 4-6 이 정한 판정을 그대로 쓴다 — `?problem_id=` 를 줬는데 항목에 `matched_order_no` **키**가 없으면
 * 이전 서버다. 그때는 목록이 아니라 오류로 세운다(`code = PAPER_USAGE_UNSUPPORTED`).
 */
import { listPapers, listFolders } from '../ExamPapersV2/examPaperApi';

/** 서버 clamp 상한(`exam_paper.py:63` — 5~100). 한 문제가 100장 넘게 담기는 일은 드물지만 페이지는 끝까지 넘긴다. */
const PER_PAGE = 100;
/** 안전장치 — 이 페이지 수를 넘으면 여기까지만 받고 「덜 봤다」고 말한다. 말없이 오래 도는 것보다 낫다. */
const MAX_PAGES = 10;

export const PAPER_USAGE_UNSUPPORTED = 'PAPER_USAGE_UNSUPPORTED';

/**
 * 배포 전 서버 판정 — 항목이 있는데 매칭 키를 가진 항목이 하나도 없다.
 * 0건은 판정하지 않는다(빈 목록은 두 서버가 같은 모양이라 가릴 수 없고, 「없음」이 사실일 수 있다).
 */
export function isLegacyListResponse(items) {
  if (!Array.isArray(items) || items.length === 0) return false;
  return !items.some((it) => it != null && Object.prototype.hasOwnProperty.call(it, 'matched_order_no'));
}

/**
 * 항목 → 화면 행. 키가 **없으면** null — 「0회 배포」와 「배포 수가 안 실려 옴」을 뭉개지 않는다
 * (요청서 §2-3 「없으면 그 칸을 그리지 않습니다」).
 */
function toRow(it) {
  return {
    paper: it,
    matchedOrderNo: it.matched_order_no,
    matchedPoints: it.matched_points ?? null,
    assignCount: it.assign_count ?? null,
  };
}

/**
 * 「이 문제」 칸 글자 — `matched_order_no` 는 0부터(회신 §1 2-2 확정)라 사람이 세는 번호로 +1.
 * 배점이 있으면 `3번 (5점)`, 없으면 `3번`. 정수가 아니면 null(칸은 '—').
 */
export function matchedLabelOfRow(row) {
  // null 을 먼저 거른다 — Number(null) 은 0 이라 그냥 세면 「1번」이 된다
  if (row == null || row.matchedOrderNo == null) return null;
  const n = Number(row.matchedOrderNo);
  if (!Number.isInteger(n)) return null;
  return row.matchedPoints != null ? `${n + 1}번 (${row.matchedPoints}점)` : `${n + 1}번`;
}

/**
 * 폴더 평면 목록 — 표가 「부모 › 자식」 경로를 그리는 재료다(PaperTable 이 buildFolderPathIndex 로 쓴다).
 * 이것을 안 넘기면 폴더 칸이 **이름 하나**로만 나와, 같은 시험지가 시험지 관리에서는 경로로,
 * 여기서는 이름으로 보인다. 실패는 삼킨다 — 폴더는 곁들이는 정보이고, 못 받아도 표는 서야 한다
 * (그때는 서버가 준 folder_name 으로 떨어진다).
 */
async function loadFoldersQuietly(getAccessToken) {
  try {
    const f = await listFolders({}, getAccessToken);
    return Array.isArray(f?.items) ? f.items : [];
  } catch (e) {
    return [];
  }
}

/**
 * 한 문제가 담긴 시험지 목록.
 * 반환 `{ rows: [{ paper, matchedOrderNo, matchedPoints, assignCount }], folders, totalItems, truncated }`.
 * 한 시험지에 같은 문제가 두 번 들어갈 수 없으므로(백엔드 `services/exampaper.py:70-72` 가 400 으로 막는다)
 * 시험지당 한 행이다. 정렬은 시험지 수정일 최신순(서버 sort).
 */
export async function fetchPapersForProblem(problemId, getAccessToken) {
  const pid = Number(problemId);
  // 정수가 아닌 값은 서버가 400 으로 되돌린다(회신 §1) — 왕복 전에 여기서 같은 말을 한다.
  if (!Number.isInteger(pid) || pid < 0) throw new Error('문제 번호가 정수가 아닙니다.');

  const query = { problemId: pid, perPage: PER_PAGE, sort: 'updated_at', order: 'desc' };
  // 목록과 폴더는 서로 모른다 — 나란히 보낸다(직렬이면 왕복 시간이 그대로 더해진다).
  const [first, folders] = await Promise.all([
    listPapers({ ...query, page: 1 }, getAccessToken),
    loadFoldersQuietly(getAccessToken),
  ]);
  if (isLegacyListResponse(first.items)) {
    const err = new Error('서버가 아직 문제 번호로 시험지를 찾지 못합니다(역조회 배포 전). 배포 뒤 다시 열어 주세요.');
    err.code = PAPER_USAGE_UNSUPPORTED;
    throw err;
  }

  let items = [...first.items];
  const totalPages = first.totalPages ?? 1;
  const lastPage = Math.min(totalPages, MAX_PAGES);
  for (let p = 2; p <= lastPage; p += 1) {
    // eslint-disable-next-line no-await-in-loop -- 페이지는 순서대로 이어 붙인다(정렬을 서버가 하므로 순서가 곧 결과다)
    const d = await listPapers({ ...query, page: p }, getAccessToken);
    items = items.concat(d.items);
  }

  return {
    rows: items.map(toRow),
    folders,
    totalItems: first.totalItems ?? items.length,
    truncated: totalPages > MAX_PAGES,
  };
}
